// Facebook Pixel Analytics Module
// It is loaded by GTM

interface QueuedEvent {
  eventName: string;
  properties: Record<string, any>;
}

const isFbqAvailable = (): boolean => typeof window !== 'undefined' && typeof window.fbq === 'function';

const sendEvent = (eventName: string, properties: Record<string, any>): void => {
  window?.fbq?.('track', eventName, properties);
};

export class FacebookPixel {
  private eventQueue: QueuedEvent[] = [];
  private isPolling: boolean = false;
  private pollingInterval: number = 100; // Check every 100ms
  private pollingTimeout: number = 60000; // Stop polling after 60 seconds
  private pollingTimer: ReturnType<typeof setInterval> | null = null;
  private pollingStartTime: number = 0;

  constructor() {
    this.startPollingIfNeeded();
  }

  private startPollingIfNeeded(): void {
    if (!isFbqAvailable() && !this.isPolling) {
      this.isPolling = true;
      this.pollingStartTime = Date.now();
      this.pollingTimer = setInterval(() => this.checkFbqAndProcessQueue(), this.pollingInterval);
    }
  }

  private checkFbqAndProcessQueue(): void {
    if (isFbqAvailable()) {
      this.processQueue();
      this.stopPolling();
    } else if (Date.now() - this.pollingStartTime > this.pollingTimeout) {
      console.warn('Facebook Pixel: Timed out waiting for fbq to become available'); // eslint-disable-line no-console
      this.stopPolling();
    }
  }

  private stopPolling(): void {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }

    this.isPolling = false;
  }

  private processQueue(): void {
    // Process events one by one to handle new events being added during processing
    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift();

      if (event) {
        sendEvent(event.eventName, event.properties);
      }
    }
  }

  private addToQueue(event: QueuedEvent): void {
    this.eventQueue.push(event);
    this.startPollingIfNeeded();
  }

  track(eventName: string, properties: Record<string, any> = {}): void {
    if (isFbqAvailable() && this.eventQueue.length === 0) {
      // If fbq is available and queue is empty, send directly
      sendEvent(eventName, properties);
    } else {
      // Otherwise queue the event
      this.addToQueue({
        eventName,
        properties,
      });
    }
  }
}
